/* assets/ab-variant.js */
(() => {
  "use strict";

  // ===========================
  // Config (overridable in theme.liquid)
  // ===========================
  const COOKIE_NAME = (window.AB_COOKIE_NAME || "ab_variant_percentage").trim();
  // ! DO NOT EVER CHANGE THE DEFAULT FOR CAMPAIGN_KEY
  const CAMPAIGN_KEY = (window.AB_CAMPAIGN_KEY || "campaign_id").trim();
  const SECTIONS_TO_REFRESH = Array.isArray(window.AB_SECTIONS_TO_REFRESH)
    ? window.AB_SECTIONS_TO_REFRESH
    : [];

  const COOKIE_MAX_AGE_DAYS = 365 * 2; // refresh each load; long-lived first-party cookie
  const PERCENT_MIN = 0;
  const PERCENT_MAX = 99; // align with Liquid's modulo: 100 (0..99)
  const VALID_CAMPAIGN_REGEX = /^[a-zA-Z0-9_-]+$/;

  // Optional: allow QA override with ?abpct=42 or ?ab_variant_percentage=42
  const QUERY_OVERRIDE_KEYS = ["abpct", COOKIE_NAME];

  // ===========================
  // Host-Only Cookie Management (100% Isolated)
  // ===========================
  
  /**
   * Sets a cookie scoped to the current hostname ONLY
   * No domain attribute = host-only cookie (no subdomain sharing)
   */
  function setHostOnlyCookie(name, value, days) {
    const parts = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      "Path=/",
      "Secure",
      "SameSite=Lax",
    ];
    
    if (days && days > 0) {
      const maxAge = Math.floor(days * 86400);
      const expires = new Date(Date.now() + maxAge * 1000).toUTCString();
      parts.push(`Max-Age=${maxAge}`, `Expires=${expires}`);
    } else {
      // Delete cookie
      parts.push("Max-Age=0", `Expires=${new Date(0).toUTCString()}`);
    }
    
    // CRITICAL: No Domain attribute = host-only cookie
    // This ensures shop.create.one and kigo.shop.create.one are 100% isolated
    document.cookie = parts.join("; ");
  }

  function getCookie(name) {
    const rx = new RegExp(
      "(?:^|; )" + name.replace(/[$()*+./?[\\\]^{|}-]/g, "\\$&") + "=([^;]*)"
    );
    const m = document.cookie.match(rx);
    return m ? decodeURIComponent(m[1]) : null;
  }

  /**
   * Deletes cookie from current domain only
   */
  function deleteCurrentDomainCookie(name) {
    setHostOnlyCookie(name, "", 0);
  }

  /**
   * Ensures a cookie is set for the current domain only with optional companion expiry tracking
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Expiration days
   * @param {boolean} useCompanionCookie - Whether to create a companion _expires_at cookie
   * @returns {object} Result with domain and scope info
   */
  function ensureIsolatedCookie(name, value, days, useCompanionCookie = false) {
    setHostOnlyCookie(name, value, days);

    // Verify cookie is readable
    const ok = getCookie(name) === String(value);
    if (!ok) {
      console.warn(`[AB] Failed to set cookie ${name} on ${location.hostname}`);
      return { domain: location.hostname, scope: "failed", success: false };
    }
    
    // Store the expiration timestamp in a companion cookie, but only for specified cookies
    if (useCompanionCookie && days > 0) {
      const expiryTimestamp = Math.floor(Date.now()/1000 + days*86400);
      setHostOnlyCookie(`${name}_expires_at`, expiryTimestamp, days);
    }
    
    return { domain: location.hostname, scope: "host-only", success: true };
  }

  // ===========================
  // Value assignment (stable %)
  // ===========================
  function randomPercentage(min = PERCENT_MIN, max = PERCENT_MAX) {
    return String(Math.floor(Math.random() * (max - min + 1)) + min);
  }

  function getQueryOverride() {
    try {
      const params = new URLSearchParams(location.search);
      for (const k of QUERY_OVERRIDE_KEYS) {
        if (params.has(k)) {
          const v = params.get(k);
          if (v != null && v !== "" && !isNaN(Number(v))) {
            return String(Math.max(PERCENT_MIN, Math.min(PERCENT_MAX, Number(v))));
          }
        }
      }
    } catch { /* ignore */ }
    return null;
  }

  function chooseValue() {
    // Priority: query override > cookie > localStorage > Liquid initial > random
    const q = getQueryOverride();
    if (q !== null) return q;

    const c = getCookie(COOKIE_NAME);
    if (c != null && c !== "" && !isNaN(Number(c))) return String(Number(c));

    try {
      const ls = localStorage.getItem(COOKIE_NAME);
      if (ls != null && ls !== "" && !isNaN(Number(ls))) return String(Number(ls));
    } catch { /* ignore */ }

    // Check for Liquid-generated initial value
    if (typeof window.INITIAL_AB_VARIANT !== 'undefined' && window.INITIAL_AB_VARIANT !== null) {
      return String(window.INITIAL_AB_VARIANT);
    }

    return randomPercentage();
  }

  // ===========================
  // Campaign ID (URL → cookie + LS → Liquid)
  // ===========================
  function getUrlParam(name) {
    try {
      const params = new URLSearchParams(location.search);
      if (!params.has(name)) return null;
      const raw = params.get(name);
      if (raw == null || raw === "") return null;
      return String(raw).trim();
    } catch {
      return null;
    }
  }

  function extractCampaignFromPath() {
    try {
      const path = location.pathname;
      
      // Check if we're on preview page (base or with A/B variant)
      if (path === '/pages/ab-preview' || path.startsWith('/pages/ab-preview/')) {
        // Extract A/B variant from preview path
        if (path.startsWith('/pages/ab-preview/')) {
          const campaign = path.replace('/pages/ab-preview/', '').split('/')[0];
          const trimmed = campaign && campaign.trim() ? campaign.trim() : null;
          // Validate before returning
          return trimmed && VALID_CAMPAIGN_REGEX.test(trimmed) ? trimmed : null;
        }
        return null; // base preview page, no variant
      }
      
      // Check if we're on a product page with potential A/B variant
      if (path.startsWith('/products/')) {
        const pathParts = path.split('/');
        if (pathParts.length >= 4) {
          // We have: ['', 'products', '<pid>', '<variant>___<collection>']
          const lastSegment = pathParts[3];
          
          if (lastSegment.endsWith('___')) {
            // Format: <variant>___ (ends with separator, no collection)
            const variant = lastSegment.replace('___', '');
            const trimmed = variant && variant.trim() ? variant.trim() : null;
            // Validate before returning
            return trimmed && VALID_CAMPAIGN_REGEX.test(trimmed) ? trimmed : null;
          } else if (lastSegment.includes('___')) {
            // Format: <variant>___<collection>
            const [variant] = lastSegment.split('___');
            const trimmed = variant && variant.trim() ? variant.trim() : null;
            // Validate before returning
            return trimmed && VALID_CAMPAIGN_REGEX.test(trimmed) ? trimmed : null;
          }
        }
      }
      
      return null; // no variant found
    } catch {
      return null;
    }
  }

  function chooseCampaignValue() {
    // Priority: URL query param > URL path > cookie > localStorage > null (don't invent)
    let id;

    id = getUrlParam(CAMPAIGN_KEY);
    if (id && VALID_CAMPAIGN_REGEX.test(id)) {
      return id;
    }

    id = extractCampaignFromPath();
    if (id && VALID_CAMPAIGN_REGEX.test(id)) {
      return id;
    }

    id = getCookie(CAMPAIGN_KEY);
    if (id && VALID_CAMPAIGN_REGEX.test(id)) {
      return String(id);
    }

    try {
      id = localStorage.getItem(CAMPAIGN_KEY);
      if (id && VALID_CAMPAIGN_REGEX.test(id)) {
        return String(id);
      }
    } catch { /* ignore */ }

    return null;
  }

  // ===========================
  // Cart attribute sync (Liquid access)
  // ===========================
  async function getCartAttributes() {
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) return {};
      const cart = await res.json();
      return cart.attributes || {};
    } catch {
      return {};
    }
  }

  async function setCartAttribute(attributes) {
    try {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ attributes }), // Pass the object directly
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  async function refreshSections(ids) {
    if (!ids || !ids.length) return;
    const url = `${location.pathname}?sections=${encodeURIComponent(ids.join(","))}`;
    try {
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return;
      const htmlById = await res.json(); // { [sectionId]: "<section...>...</section>" }
      ids.forEach((id) => {
        const sel =
          document.querySelector(`[data-section-id="${id}"]`) ||
          document.querySelector(`#shopify-section-${id}`);
        if (sel && htmlById[id]) {
          sel.outerHTML = htmlById[id];
        }
      });
    } catch { /* ignore */ }
  }

  // ===========================
  // Calculate campaign-specific expiry
  // ===========================
  function getCampaignExpiryDays(campaignId) {
    // First try to get campaign-specific expiry hours from the map
    if (
      window.AB_CAMPAIGN_EXPIRY_HOURS &&
      campaignId &&
      Object.prototype.hasOwnProperty.call(window.AB_CAMPAIGN_EXPIRY_HOURS, campaignId)
    ) {
      const hours = Number(window.AB_CAMPAIGN_EXPIRY_HOURS[campaignId]);
      if (!isNaN(hours) && hours > 0) {
        return Math.min(hours / 24, COOKIE_MAX_AGE_DAYS);
      }
    }
    
    // For campaigns we know are defaults (but might not have expiry hours set)
    if (isDefaultCampaign(campaignId, window.MARKETPLACE_NAME)) {
      // Defaults should be long-lived
      return COOKIE_MAX_AGE_DAYS;
    }
    
    // For unknown non-default campaigns: short safety window
    return 1; // 1 day
  }

  // ===========================
  // Helper to check default campaign
  // ===========================
  function handleize(str){
    return str
      .toLowerCase()
      .trim()
      .replace(/['"]/g,'')
      .replace(/[^a-z0-9]+/g,'-')
      .replace(/^-+|-+$/g,'');
  }
  
  function isDefaultCampaign(id, marketplaceName){
    if(!id||!marketplaceName) return false;
    return handleize(id) === handleize(marketplaceName + '_default');
  }

  // ===========================
  // Init
  // ===========================
  (async function init() {
    console.log(`[AB] Initializing on ${location.hostname} with isolated cookies`);
    
    // -------- AB % assignment --------
    let value = chooseValue();
    const cookieResult = ensureIsolatedCookie(COOKIE_NAME, value, COOKIE_MAX_AGE_DAYS);
    window.ab_variant_percentage = Number(value);
    document.documentElement.dataset.abVariantPercentage = value;
    document.documentElement.style.setProperty("--ab-variant-percentage", value);
    window.dispatchEvent(new CustomEvent("ab:ready", { detail: { value: Number(value), cookie: cookieResult } }));

    const attrs = await getCartAttributes();
    const attributesToUpdate = {};

    // Mirror AB % to cart.attributes for Liquid (avoid redundant writes)
    if (String(attrs[COOKIE_NAME] || "") !== String(value)) {
      attributesToUpdate[COOKIE_NAME] = value;
    }

    // -------- Campaign ID capture --------
    let campaignVal = chooseCampaignValue();
    const campaignInCart = attrs[CAMPAIGN_KEY];

    // --- Scenario 1: Cookie expired, clear cart attributes ---
    if (campaignVal === null && campaignInCart) {
      const clearAttrs = {
        [CAMPAIGN_KEY]: null,
        [`${CAMPAIGN_KEY}_expires_at`]: null
      };
      if (String(attrs[COOKIE_NAME] || "") !== String(value)) {
        clearAttrs[COOKIE_NAME] = value;
      }
      
      // Add marketplace config attributes
      if (window.MARKETPLACE_CONFIG) {
        clearAttrs.marketplace_name = window.MARKETPLACE_CONFIG.name || null;
        clearAttrs.marketplace_logo_url = window.MARKETPLACE_CONFIG.logo_url || null;
      }
      
      const ok = await setCartAttribute(clearAttrs);
      if (ok) {
        document.documentElement.removeAttribute('data-campaign-id');
        delete window.campaign_id;
      }
      return;
    }

    // --- Campaign URL & Cookie Handling (Isolated) ---
    const campaignFromUrl = getUrlParam(CAMPAIGN_KEY) || extractCampaignFromPath();
    const existingCampaignCookie = getCookie(CAMPAIGN_KEY);

    // Only update campaign value if URL has a valid one that's different
    if (campaignFromUrl !== null && VALID_CAMPAIGN_REGEX.test(campaignFromUrl)) {
      // If from URL differs from existing cookie, update the cookie with new expiry
      if (campaignFromUrl !== existingCampaignCookie) {
        campaignVal = campaignFromUrl;
        const campaignExpiryDays = getCampaignExpiryDays(campaignVal);
        ensureIsolatedCookie(CAMPAIGN_KEY, campaignVal, campaignExpiryDays, true);
      }
    } 
    // If we have a valid campaign but no cookie yet (first visit), set the cookie
    else if (campaignVal !== null && VALID_CAMPAIGN_REGEX.test(campaignVal) && !existingCampaignCookie) {
      const campaignExpiryDays = getCampaignExpiryDays(campaignVal);
      ensureIsolatedCookie(CAMPAIGN_KEY, campaignVal, campaignExpiryDays, true);
    }

    // --- Check if cart sync is needed for the campaign ---
    if (
      campaignVal !== null &&
      (
        // 1. Campaign changed
        String(attrs[CAMPAIGN_KEY] || "") !== String(campaignVal) ||
        // 2. Missing expiry attribute
        !attrs[`${CAMPAIGN_KEY}_expires_at`] ||
        // 3. Non-default campaign has invalid (0) expiry
        (!isDefaultCampaign(campaignVal, window.MARKETPLACE_NAME) &&
          String(attrs[`${CAMPAIGN_KEY}_expires_at`]) === "0") ||
        // 4. Default campaign incorrectly has a positive expiry (needs reset to 0)
        (isDefaultCampaign(campaignVal, window.MARKETPLACE_NAME) &&
          attrs[`${CAMPAIGN_KEY}_expires_at`] &&
          Number(attrs[`${CAMPAIGN_KEY}_expires_at`]) > 0)
      )
    ) {
      const defaultLike = isDefaultCampaign(campaignVal, window.MARKETPLACE_NAME);
      let expiresAtTimestamp = 0;
      if (!defaultLike) {
        // Check for existing expiry timestamp first
        expiresAtTimestamp = getCookie(`${CAMPAIGN_KEY}_expires_at`);
        
        // Only recalculate if missing
        if (!expiresAtTimestamp) {
          const campaignExpiryDays = getCampaignExpiryDays(campaignVal);
          ensureIsolatedCookie(CAMPAIGN_KEY, campaignVal, campaignExpiryDays, true);
          expiresAtTimestamp = Math.floor((Date.now() / 1000) + (campaignExpiryDays * 86400));
        }
      }
      attributesToUpdate[CAMPAIGN_KEY] = campaignVal;
      attributesToUpdate[`${CAMPAIGN_KEY}_expires_at`] = expiresAtTimestamp;
    }

    // Handle campaign expire after purchase
    if (campaignVal !== null && VALID_CAMPAIGN_REGEX.test(campaignVal) && 
        window.AB_CAMPAIGN_EXPIRE_AFTER_PURCHASE && 
        window.AB_CAMPAIGN_EXPIRE_AFTER_PURCHASE[campaignVal]) {
      
      // Always update the pending expire cookie with the current campaign's expiry
      const campaignExpiryDays = getCampaignExpiryDays(campaignVal);
      ensureIsolatedCookie(`${CAMPAIGN_KEY}_pending_expire`, "1", campaignExpiryDays);
      
    } else {
      // Clean up any old pending expire flags
      deleteCurrentDomainCookie(`${CAMPAIGN_KEY}_pending_expire`);
    }

    // --- Perform a single cart update if anything changed ---
    if (Object.keys(attributesToUpdate).length > 0) {
      // Add marketplace config attributes to EVERY cart update
      if (window.MARKETPLACE_CONFIG) {
        attributesToUpdate.marketplace_name = window.MARKETPLACE_CONFIG.name || null;
        attributesToUpdate.marketplace_logo_url = window.MARKETPLACE_CONFIG.logo_url || null;
      }
      
      const ok = await setCartAttribute(attributesToUpdate);
      if (ok && SECTIONS_TO_REFRESH.length) {
        refreshSections(SECTIONS_TO_REFRESH);
      }
    } else {
      // Even if no other attributes changed, ensure marketplace attributes are set
      if (window.MARKETPLACE_CONFIG) {
        const marketplaceAttrs = {
          marketplace_name: window.MARKETPLACE_CONFIG.name || null,
          marketplace_logo_url: window.MARKETPLACE_CONFIG.logo_url || null
        };
        
        // Only update if values have changed
        if (
          String(attrs.marketplace_name || "") !== String(marketplaceAttrs.marketplace_name) ||
          String(attrs.marketplace_logo_url || "") !== String(marketplaceAttrs.marketplace_logo_url)
        ) {
          await setCartAttribute(marketplaceAttrs);
        }
      }
    }

    // Expose campaign ID to JS & HTML dataset
    if (campaignVal !== null) {
      window.campaign_id = campaignVal;
      document.documentElement.dataset.campaignId = campaignVal;
      window.dispatchEvent(new CustomEvent("ab:campaign", { detail: { key: CAMPAIGN_KEY, value: campaignVal } }));
    }

    console.log(`[AB] Initialized: variant=${value}%, campaign=${campaignVal || 'none'}, domain=${location.hostname}`);
  })();
})();